import { useState } from "react";
import { SeoHead } from "@/components/SeoHead";
import { getSeoCopy } from "@/i18n/seoCopy";
import { useI18n } from "@/i18n";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { LocalizedLink } from "@/components/LocalizedLink";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import rem7Placeholder from "@/assets/hero-luxury-bathroom.jpg";

type Item = { name: string; line: string; img: string; href: string; cat: Cat };
type Cat = "rem7" | "signature" | "bath";

// Images are mapped only to the product pages they already belong to on this site.
const items: Item[] = [
  { name: "REM7", line: "Flagship · product imagery coming soon", img: rem7Placeholder, href: "/products/rem7", cat: "rem7" },
  { name: "REM7 M3", line: "REM7 range", img: "/images/products/m3-hero-main.jpg", href: "/products/m3", cat: "rem7" },
  { name: "REM7 S1", line: "REM7 range", img: "/images/products/s1-hero-egg.jpg", href: "/products/s1", cat: "rem7" },
  { name: "REM7 K2", line: "REM7 range", img: "/images/products/k2-hero-desert.jpg", href: "/products/k2", cat: "rem7" },
  { name: "REM7 U1", line: "REM7 range", img: "/images/products/u1-product-main.jpg", href: "/products/u1", cat: "rem7" },
  { name: "REM7 L1", line: "REM7 range", img: "/images/products/l1-product-main.png", href: "/products/l1", cat: "rem7" },
  { name: "LUXA", line: "Signature collection", img: "/images/products/luxa-series-product-side.jpg", href: "/products/luxa", cat: "signature" },
  { name: "LUNA", line: "Signature collection", img: "/images/products/luna-series-product-main.jpg", href: "/products/luna", cat: "signature" },
  { name: "NOVA", line: "Signature collection", img: "/images/products/nova-series-product-main.jpg", href: "/products/nova", cat: "signature" },
  { name: "ARIA", line: "Signature collection", img: "/images/products/aria-series-product-main.jpg", href: "/products/aria", cat: "signature" },
  { name: "VENA", line: "Signature collection", img: "/images/products/vena-series-product.jpg", href: "/products/vena", cat: "signature" },
  { name: "AURELIUS", line: "Signature collection", img: "/images/products/aurelius-series-product-main.jpg", href: "/products/aurelius", cat: "signature" },
  { name: "AURELUX", line: "Signature collection", img: "/images/products/aurelux-series-product-main.jpg", href: "/products/aurelux", cat: "signature" },
  { name: "LUMA", line: "Signature collection", img: "/images/products/luma-series-product.jpg", href: "/products/luma", cat: "signature" },
  { name: "ARIA Diamond", line: "Bathtub", img: "/images/bath/aria-diamond-hero.jpg", href: "/bath-sanitary/aria-diamond", cat: "bath" },
  { name: "ARIA Sculpt", line: "Bathtub", img: "/images/bath/aria-sculpt-hero.jpg", href: "/bath-sanitary/aria-sculpt", cat: "bath" },
  { name: "ARIA Monolith", line: "Bathtub", img: "/images/bath/aria-monolith-villa.jpg", href: "/bath-sanitary/aria-monolith", cat: "bath" },
];

const cats: { id: Cat | "all"; label: string }[] = [
  { id: "all", label: "All" }, { id: "rem7", label: "REM7 range" },
  { id: "signature", label: "Signature smart toilets" }, { id: "bath", label: "Bath & sanitary" },
];

export default function Collection() {
  const { language } = useI18n();
  const seo = getSeoCopy("collection", language);
  const [cat, setCat] = useState<Cat | "all">("all");
  const shown = cat === "all" ? items : items.filter((i) => i.cat === cat);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SeoHead
        route="collection"
        schema={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.aureum7trading.com/" },
            { "@type": "ListItem", "position": 2, "name": "Collection", "item": "https://www.aureum7trading.com/collection" }
          ]
        }}
      />
      <Navigation />
      <main>
        <section className="bg-primary pt-32 pb-14 text-primary-foreground sm:pt-40">
          <div className="container mx-auto max-w-5xl px-4 text-center sm:px-6">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.25em] text-accent">New collection · arriving soon</p>
            <h1 className="font-playfair text-4xl leading-tight sm:text-6xl">{seo.h1}</h1>
            <p className="mx-auto mt-5 max-w-2xl text-primary-foreground/75">Smart toilets and bathroom pieces for premium homes, hotels and residential projects. Product details, availability and trade terms are confirmed on request.</p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90"><LocalizedLink to="/contact?intent=trade#enquiry-form">Request Trade Pricing</LocalizedLink></Button>
              <Button asChild size="lg" variant="outline" className="border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10"><LocalizedLink to="/contact?intent=waitlist#enquiry-form">Join Demo Waitlist</LocalizedLink></Button>
            </div>
          </div>
        </section>

        <nav aria-label="Collection categories" className="sticky top-[72px] lg:top-[96px] z-30 border-b border-border/40 bg-background/95 backdrop-blur">
          <div className="container mx-auto flex gap-2 overflow-x-auto px-4 py-3 sm:justify-center sm:px-6">
            {cats.map((c) => (
              <button key={c.id} onClick={() => setCat(c.id)} aria-pressed={cat === c.id}
                className={cn("min-h-[44px] shrink-0 rounded-full border px-4 text-sm font-medium transition-colors",
                  cat === c.id ? "border-accent bg-accent text-accent-foreground" : "border-border text-muted-foreground hover:text-foreground")}>
                {c.label}
              </button>
            ))}
          </div>
        </nav>

        <section className="container mx-auto px-4 py-12 sm:px-6 sm:py-16">
          <ul className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            {shown.map((i) => (
              <li key={i.href}>
                <LocalizedLink to={i.href} className="group block overflow-hidden rounded-lg border border-border/40 bg-card focus:outline-none focus:ring-2 focus:ring-accent">
                  <div className="aspect-[4/3] sm:aspect-[4/5] overflow-hidden bg-muted">
                    <img src={i.img} alt={`${i.name} — ${i.line}`} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-3 sm:p-4">
                    <p className="text-[11px] uppercase tracking-widest text-accent">{i.line}</p>
                    <h2 className="mt-1 font-playfair text-lg text-primary sm:text-xl">{i.name}</h2>
                    <span className="mt-2 inline-block text-sm text-muted-foreground group-hover:text-accent">View details →</span>
                  </div>
                </LocalizedLink>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-center text-sm text-muted-foreground">
            <LocalizedLink to="/bath-sanitary" className="text-accent hover:underline">See the full bath & sanitary range →</LocalizedLink>
          </p>
        </section>

        <section className="border-t border-border/30 bg-muted/30 py-14 sm:py-20">
          <div className="container mx-auto max-w-3xl px-4 text-center sm:px-6">
            <h2 className="font-playfair text-3xl text-primary sm:text-4xl">Request information on the new collection</h2>
            <p className="mt-4 text-muted-foreground">Tell us about your project and we will share product information and next steps when they are confirmed. No payment or reservation is required.</p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Button asChild size="lg"><LocalizedLink to="/contact?intent=trade#enquiry-form">Request Trade Pricing</LocalizedLink></Button>
              <Button asChild size="lg" variant="outline"><LocalizedLink to="/contact?intent=consultation#enquiry-form">Book Consultation</LocalizedLink></Button>
              <Button asChild size="lg" variant="outline"><LocalizedLink to="/contact?intent=waitlist#enquiry-form">Join Demo Waitlist</LocalizedLink></Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
